<template>
  <div class="privacy">
    <H1>Dremca Privacy Policy</H1>
    <section class="privacy-section" aria-labelledby="region1">
      <h2 id="region1">Purpose of this notice</h2>
      <p>
        Title V of the Gramm-Leach-Bliley Act (GLBA) and the laws of the Commonwealth of Massachusetts, generally prohibit us from sharing nonpublic personal information about you with a third party unless we provide you with this notice of our privacy policies and practices describing the type of information that we collect about you and the categories of persons or entities to whom that information may be disclosed, and in certain circumstances, obtain your consent or permit you to opt-out of such disclosure. In compliance with the GLBA and the laws of this Commonwealth, we are providing you with this document, which notifies you of the privacy policies and practices of Dremca.
      </p>
      <p>
        Our Privacy Policy is current as of the date at the top, but it may change from time to time. We may e-mail periodic reminders of our notices and conditions, unless you have instructed us not to, but you should check our Web site frequently to see recent changes. Unless stated otherwise, our current Privacy Notice applies to all information that we have about you and your accounts. We stand behind the promises we make, however, and will not materially change our policies and practices to make them less protective of customer information collected in the past without the consent of affected customers.
      </p>
    </section>
    <section class="privacy-section" aria-labelledby="region2">
      <h2 id="region2">Your privacy is our concern</h2>
      <p>
        When you apply for insurance at Dremca, you disclose a certain amount of information about yourself to us. Law regulates the collection, use and disclosure of such information. In addition, the employees of Dremca recognize the confidentiality of the information. Therefore, it is the policy of Dremca to:
        <ul>
          <li>Collect only information necessary or relevant to our business;</li>
          <li>Make a reasonable effort to ensure that information we act upon is accurate, relevant, timely and complete;</li>
          <li>Use only legitimate means to collect information;</li>
          <li>Make personal information available externally only to respond to legitimate business needs, to regulatory or other governmental authorities or as otherwise permitted by law;</li>
          <li>Limit employee access to those who have a business need for the information.</li>
        </ul>
      </p>
      <p>We are providing you with the following information Dremca or it’s employees may collect, what is done with information after it is collected and how you can find out about information, if any, we have about you in our records.</p>
    </section>
    <section class="privacy-section" aria-labelledby="region3">
      <h2 id="region3">What kind of information do we collect about you and from whom?</h2>
      <p>
        We obtain most of the information directly from you. The application you complete, as well as any additional information you provide, generally gives us most of the information we need to know. Sometimes we may contact you by phone, fax, email or mail to obtain additional information.
      </p>
      <p>
        Depending on the nature of your insurance transaction, we may need additional information about you or other individuals proposed for coverage. For auto insurance, we may need information from motor vehicle records. For property coverages, we may send someone to inspect your property and verify information about its value and condition. A photo of any property to be insured might be taken. We may review insurance claims information and may, if necessary obtain medical information. We may also obtain additional information we need from third parties, such as other insurance agencies or companies.
      </p>
    </section>
    <section class="privacy-section" aria-labelledby="region4">
      <h2 id="region4">What do we do with the information collected about you?</h2>
      <p>
        Information that has been collected about you, which we retain, is maintained in our files. We review it in evaluating your request for insurance coverage and in determining your rates. We will also refer to and use information in our policy records for purposes related to issuing and servicing insurance policies and settling claims. We may use information about you in our files for insurance marketing purposes or to help you with your overall insurance program.
      </p>
    </section>
    <section class="privacy-section" aria-labelledby="region5">
      <h2 id="region5">To whom do we disclose information about you?</h2>
      <p>
        We usually will not disclose information about you to others unless disclosure is necessary to conduct our business. By law, we are also permitted to share information about you under circumstances to certain persons and organizations such as:
        <ul>
          <li>Our affiliated companies;</li>
          <li>
            Insurance companies we represent;
          </li>
          <li>
            Independent claims adjusters, appraisers, investigators and attorneys who need the information to investigate, defend or settle a claim involving you;
          </li>
          <li>
            Businesses that help us with data processing or marketing;
          </li>
          <li>
            Other insurance companies, agents or consumer reporting agencies as reasonably necessary in connection with any application, policy or claim involving you;
          </li>
          <li>
            Insurance support organizations which are established to collect information for the purpose of detecting insurance crimes of fraudulent claims;
          </li>
          <li>
            Medical care institutions or medical professionals to verify coverage;
          </li>
          <li>
            Insurance regulatory agencies in connection with the regulation of our business;
          </li>
          <li>
            Authorized persons as ordered by a subpoena, warrant or other court order or as required by law;
          </li>
          <li>
            Certificate holders or policyholders for the purpose of providing information regarding the status of an insurance transaction; or
          </li>
          <li>
            Lien holders, mortgagors, lessors or other persons shown on our records as having a legal or beneficial interest in your policy.
          </li>
        </ul>
      </p>
    </section>
    <section class="privacy-section" aria-labelledby="region6">
      <h2 id="region6">Your right to access and amend your personal information</h2>
      <p>
        You have the right to request access to the personal information that we record about you. Your right includes the right to know the source of the information and the identity of the persons, institutions or types of institutions to whom we have disclosed such information within 2 years prior to your request. Your right includes the right to view such information and copy it in person, or request that a copy of it be sent to you by mail (for which we may charge you a reasonable fee to cover our costs). Your right also includes the right to request corrections, amendments or deletions of any information in our possession. The procedures that you must follow to request access to or amend your information are as follows:
      </p>
      <p>
        To obtain access to your information: You should submit a request in writing to Customer Privacy Inquiries, PO Box 296, Stow, MA 01775. The request should include your name, address, social security number, telephone number, and the recorded information to which you would like access. The request would state whether you would like access in person or a copy of the information sent to you by mail. Upon receipt of your request, we will contact you within 30 business days to arrange providing you with access in person or the copies that you have requested.
      </p>
      <p>
        To correct, amend, or delete any of your information: You should submit a request in writing to Customer Privacy Inquiries, PO Box 296, Stow, MA 01775. The request should include your name, address, social security number, telephone number, the specific information in dispute, and the identity of document or record that contains the disputed information. Upon receipt of your request, we will contact you within 30 business days to notify you either that we have made the correction, amendment or deletion, or that we refuse to do so and the reasons for the refusal, which you will have an opportunity to challenge.
      </p>
    </section>
    <section class="privacy-section" aria-labelledby="region7">
      <h2 id="region7">Our practices regarding information confidentiality and security</h2>
      <p>
        We restrict access to nonpublic personal information about you to those employees who need to know that information in order to provide products or services to you. Dremca has implemented a comprehensive written information security program (WISP) to ensure compliance with federal and state regulations, including Gramm-Leach Bliley and Massachusetts data security regulation, 201 CMR 17.00.  We utilize physical, electronic, and procedural safeguards to maintain the confidentiality, security and integrity of personal information (as defined in 201 CMR 17.00) in our possession, and to protect against unauthorized access and disclosure. 
      </p>
    </section>
    <section class="privacy-section" aria-labelledby="region8">
      <h2 id="region8">Reservation of the right to disclose information in unforeseen circumstances</h2>
      <p>
        In connection with the potential sale or transfer of its interests, Dremca, and its affiliates (if any) reserve the right to sell or transfer your information (including but not limited to your address, name, age, sex, zip code, state and country of residency and other information that you provide through other communications) to a third party entity that:
      </p>
      <p>
        (1) Concentrates its business in a similar practice or service; (2) agrees to be Dremca’s successor in interest with regard to the maintenance and protection of the information collected; and (3) agrees to the obligations of this privacy statement.
      </p>
    </section>
  </div>
</template>

<script>
import {
  H1
} from "../../style";
export default {
  name: "Privacy",
  components: {
    H1
  }
}
</script>

<style lang="scss" scoped>
@import "~@/style/_variables.scss";
.privacy {
  margin: 5rem auto;
  height: auto;
  box-sizing: border-box;
  width: 100%;
  max-width: 900px;
  padding: $padding-xxl 4rem;
  color: #294154;
  background-color: $white;
  border-radius: $border-radius-lg;
  
  &-section {
    p, li {
      color: $body-gray;
      line-height: 1.77;
    }
    h2 {
      margin: $padding-xxl 0 $padding-lg; 
      font-weight: 400;
    }
  }

  @media (max-width: $mobile-hd-width) {
    padding: 2rem;
  }
}
</style>